/**
 * Which build of the web app is this?
 *
 * Both values are baked in by `vite.config.ts` at build time (`define`), because the browser has no
 * git and the bundle is a static artifact: anything not resolved during the build is unknowable
 * afterwards. They answer different questions, which is why there are two:
 *
 * - `version` — what a person says out loud. "We're on 1.4.0." A sha cannot do this; nobody can
 *   tell whether `4a3f812` is newer than `d2c4f15`.
 * - `sha` — the only value that reaches an exact point in git history, and the reason this exists.
 *   A `-dirty` suffix means the build came from a tree with uncommitted changes, so the commit it
 *   names does NOT describe what shipped.
 *
 * This is the app's OWN identity, not the API's. The two deploy independently — on 2026-09-21 the
 * API ran `4a3f8129` while the frontend was still serving `bdf3a905` — so a single number would
 * have been wrong for one of them. The API reports itself via `X-Build-Id` and `GET /version`.
 *
 * Accessor, not raw globals, for the same reason `runtimeConfig.ts` is one: a `__GLOBAL__` read
 * scattered through components is impossible to stub in a test and silently becomes `undefined`
 * wherever the define did not reach (vitest, Storybook, a consumer of the source).
 */

declare const __APP_VERSION__: string;
declare const __BUILD_SHA__: string;

export interface BuildInfo {
  /** Product version from the root package.json, e.g. `1.0.0`. */
  version: string;
  /** Short commit sha, `-dirty` when built from uncommitted changes, `unknown` without git. */
  sha: string;
}

/** `typeof` guard first: under vitest the defines are absent, and a bare read would throw. */
const read = (value: () => string, fallback: string): string => {
  try {
    return value() ?? fallback;
  } catch {
    return fallback;
  }
};

export function getBuildInfo(): BuildInfo {
  return {
    version: read(() => (typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : ""), "0.0.0"),
    sha: read(() => (typeof __BUILD_SHA__ !== "undefined" ? __BUILD_SHA__ : ""), "dev"),
  };
}

/**
 * The single string shown in the footer: `1.0.0.4a3f812`.
 *
 * One token, not three fields with separators, because it is read aloud to support and pasted into
 * bug reports — and a value that survives being retyped is worth more than one that is easier to
 * lay out. It reads as an ordinary version whose last segment happens to be the commit, so the part
 * that matters to engineering travels wherever the part that matters to a person does.
 */
export function formatBuildLine(info: BuildInfo = getBuildInfo()): string {
  return `${info.version}.${info.sha}`;
}
